/* ONE Events & Experiences case study (sections/one-case-study.liquid). No dependencies. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
  const pad2 = (n) => String(n).padStart(2, '0');

  function init(root) {
    if (!root || root.dataset.ocReady) return;
    root.dataset.ocReady = 'true';
    root.classList.add('is-js');
    const cleanups = [];
    const on = (target, type, fn, opts) => {
      if (!target) return;
      target.addEventListener(type, fn, opts);
      cleanups.push(() => target.removeEventListener(type, fn, opts));
    };
    const observe = (els, fn, options) => {
      if (!('IntersectionObserver' in window)) return els.forEach((el) => fn(el, true));
      const io = new IntersectionObserver((entries) => entries.forEach((e) => fn(e.target, e.isIntersecting, io)), options);
      els.forEach((el) => io.observe(el));
      cleanups.push(() => io.disconnect());
    };
    const timers = new Set();
    const later = (fn, ms) => { const t = setTimeout(() => { timers.delete(t); fn(); }, ms); timers.add(t); return t; };
    cleanups.push(() => timers.forEach(clearTimeout));
    const ctx = { root, on, observe, cleanups, later };

    initReveal(ctx);
    initMascots(ctx);
    initHero(ctx);
    initBrand(ctx);
    initAcq(ctx);
    initAutomation(ctx);
    initResults(ctx);

    root.ocDestroy = () => cleanups.forEach((fn) => fn());
  }

  /* Arrow-key navigation + selection for role="tab" / role="radio" groups. */
  function bindGroup(buttons, select, on) {
    buttons.forEach((btn, i) => {
      on(btn, 'click', () => select(i, true));
      on(btn, 'keydown', (e) => {
        const dir = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
        if (e.key === 'Home' || e.key === 'End' || dir) {
          e.preventDefault();
          const next = e.key === 'Home' ? 0 : e.key === 'End' ? buttons.length - 1 : (i + dir + buttons.length) % buttons.length;
          select(next, true);
          buttons[next].focus();
        }
      });
    });
  }
  function markSelected(buttons, index, attr) {
    buttons.forEach((b, i) => {
      b.setAttribute(attr, String(i === index));
      b.tabIndex = i === index ? 0 : -1;
    });
  }
  /* Brief mascot reaction: swaps to the alternate pose for a moment. */
  function react(peek, later) {
    if (!peek || reduceMotion.matches) return;
    peek.classList.remove('is-reacting');
    void peek.offsetWidth;
    peek.classList.add('is-reacting');
    clearTimeout(peek._t);
    peek._t = later(() => peek.classList.remove('is-reacting'), 1300);
  }
  /* Scroll a horizontal rail so a child is visible, without moving the page. */

  /* ---------- Reveal on scroll + scene animation gating ---------- */
  function initReveal({ root, observe }) {
    observe($$('[data-reveal]', root), (el, inView, io) => {
      if (!inView) return;
      el.classList.add('is-in');
      io && io.unobserve(el);
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.12 });
    observe($$('.dcs-cta, [data-oc-res]', root), (el, inView) => el.classList.toggle('is-animating', inView), { threshold: 0.15 });
  }

  /* ---------- Trustline mascots: a short entrance once each comes into view ---------- */
  function initMascots({ root, observe }) {
    observe($$('.oc-mascot', root), (el, inView, io) => {
      if (!inView) return;
      el.classList.add('is-in');
      io && io.unobserve(el);
    }, { threshold: 0.3 });
  }

  /* ---------- Hero: same contraction behaviour as the shared Trustline case-study hero ---------- */
  function initHero({ root, on, observe }) {
    const hero = $('[data-dcs-hero]', root);
    const video = $('[data-dcs-hero-video]', root);
    const toggle = $('[data-dcs-video-toggle]', root);
    if (!hero) return;
    requestAnimationFrame(() => hero.classList.add('is-ready'));
    const update = () => {
      if (reduceMotion.matches) return hero.style.setProperty('--p', '1');
      const range = Math.max(1, hero.offsetHeight - window.innerHeight);
      const progress = clamp(-hero.getBoundingClientRect().top / range, 0, 1);
      hero.style.setProperty('--p', (1 - Math.pow(1 - progress, 2)).toFixed(4));
    };
    on(window, 'scroll', update, { passive: true });
    on(window, 'resize', update);
    update();
    if (!video || !toggle) return;
    let userPaused = false;
    const setPaused = (paused) => {
      toggle.setAttribute('aria-pressed', String(paused));
      toggle.setAttribute('aria-label', paused ? 'Play background video' : 'Pause background video');
    };
    const play = () => {
      const attempt = video.play();
      if (attempt && attempt.catch) attempt.catch(() => setPaused(true));
    };
    if (reduceMotion.matches) {
      userPaused = true;
      video.removeAttribute('autoplay');
      video.pause();
      setPaused(true);
    }
    on(reduceMotion, 'change', () => {
      update();
      if (reduceMotion.matches) { userPaused = true; video.pause(); setPaused(true); }
    });
    on(toggle, 'click', () => {
      userPaused = !video.paused;
      if (userPaused) video.pause();
      else play();
      setPaused(userPaused);
    });
    observe([hero], (_, inView) => {
      if (userPaused) return;
      if (inView) play();
      else video.pause();
    });
  }

  /* ---------- Brand to digital ---------- */
  const ROLE_CAPTIONS = {
    energy: 'Yellow carries the call to action and the highlight word, never long passages of text.',
    contrast: 'Black gives immersive sections depth and focus, so imagery and yellow details glow.',
    clarity: 'White opens up the layout and lets the project photography lead.'
  };
  function initBrand({ root, on, observe, later }) {
    const brand = $('[data-ob]', root);
    if (!brand) return;
    /* The dark feature card grows from an inset card to a full-bleed slab as it crosses the viewport, then returns. */
    const card = $('[data-ob-card]', brand);
    if (card) {
      let ticking = false;
      const update = () => {
        ticking = false;
        const r = card.getBoundingClientRect(), vh = window.innerHeight;
        const grow = clamp((vh * 0.5 - r.top) / (vh * 0.4), 0, 1), shrink = clamp((r.bottom - vh * 0.5) / (vh * 0.4), 0, 1);
        const t = Math.min(grow, shrink);
        card.style.setProperty('--p', (t * t * (3 - 2 * t)).toFixed(3));
      };
      const queue = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
      on(window, 'scroll', queue, { passive: true }); on(window, 'resize', queue);
      update();
    }
    const stage = $('[data-ob-colour]', brand);
    const roles = $$('[data-ob-role]', stage);
    const caption = $('[data-ob-caption]', stage);
    const preview = $('#ob-preview', stage);
    const CYCLE = 5200;
    let index = 0, timer = 0, auto = !reduceMotion.matches, visible = false;
    const selectRole = (i, user) => {
      index = i;
      markSelected(roles, i, 'aria-selected');
      stage.dataset.role = roles[i].dataset.obRole;
      preview.setAttribute('aria-labelledby', roles[i].id);
      caption.textContent = ROLE_CAPTIONS[stage.dataset.role];
      if (user) { auto = false; stage.removeAttribute('data-auto'); clearTimeout(timer); }
      else if (auto && visible) schedule();
    };
    const schedule = () => {
      clearTimeout(timer);
      stage.style.setProperty('--ob-cycle', CYCLE + 'ms');
      stage.removeAttribute('data-auto'); void stage.offsetWidth;
      stage.setAttribute('data-auto', '');
      timer = later(() => selectRole((index + 1) % roles.length, false), CYCLE);
    };
    bindGroup(roles, selectRole, on);
    observe([stage], (_, inView) => {
      visible = inView;
      if (!auto) return;
      if (inView) schedule();
      else { clearTimeout(timer); stage.removeAttribute('data-auto'); }
    }, { threshold: 0.45 });

    const type = $('[data-ob-type]', brand);
    const views = $$('[data-ob-type-view]', type);
    let typeTouched = false;
    const setView = (i, user) => {
      if (user) typeTouched = true;
      markSelected(views, i, 'aria-checked');
      type.dataset.state = views[i].dataset.obTypeView;
    };
    bindGroup(views, setView, on);
    observe([type], (_, inView, io) => {
      if (!inView || typeTouched) return;
      io && io.unobserve(type);
      later(() => { if (!typeTouched) setView(1, false); }, reduceMotion.matches ? 0 : 1500);
    }, { threshold: 0.5 });

    observe($$('.ob-loop', brand), (el, inView) => el.classList.toggle('is-playing', inView && !reduceMotion.matches), { threshold: 0.35 });
  }

  /* ---------- Paid acquisition: channel switcher + creative galleries ---------- */
  function initAcq({ root, on, observe, later }) {
    const section = $('[data-oc-acq]', root);
    if (!section) return;
    const tabs = $$('[data-channel]', section);
    const panes = $$('[data-ch]', section);
    const mascot = $('[data-oa-mascot]', section);
    let inView = false, channel = 0;
    const galleries = panes.map((pane) => setupGallery(pane));

    bindGroup(tabs, (i, user) => {
      channel = i;
      markSelected(tabs, i, 'aria-selected');
      panes.forEach((p, j) => { p.hidden = j !== i; p.classList.toggle('is-active', j === i); });
      $('.oc-acq__switch', section).style.setProperty('--i', i);
      galleries.forEach((g, j) => g.setVisible(inView && j === i));
      if (user && mascot && !reduceMotion.matches) { mascot.classList.remove('is-waving'); void mascot.offsetWidth; mascot.classList.add('is-waving'); }
    }, on);
    observe([$('[data-oa-panel]', section)], (_, v) => { inView = v; galleries.forEach((g, j) => g.setVisible(v && j === channel)); }, { threshold: 0.25 });
    on(document, 'visibilitychange', () => galleries.forEach((g, j) => g.setVisible(inView && j === channel && !document.hidden)));

    function setupGallery(pane) {
      const gallery = $('[data-oa-gallery]', pane);
      const items = $$('[data-oa-item]', gallery);
      const strip = $('[data-oa-thumbs]', gallery);
      const vids = $$('[data-oa-vid]', gallery).map((box) => ({ box, video: $('video', box), play: $('[data-oa-play]', box), paused: reduceMotion.matches }));
      let active = 0, visible = false;

      const slides = items.map((item, j) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'oa-slide';
        b.setAttribute('role', 'tab');
        b.setAttribute('aria-selected', String(j === 0));
        b.tabIndex = j === 0 ? 0 : -1;
        b.textContent = item.dataset.label;
        strip.append(b);
        return b;
      });

      const ensureSrc = (v) => { if (v.video.dataset.src && !v.video.getAttribute('src')) { v.video.src = v.video.dataset.src; v.video.load(); } };
      vids.forEach((v) => {
        const sync = () => { const p = v.video.paused; v.play.setAttribute('aria-pressed', String(p)); v.play.setAttribute('aria-label', p ? 'Play video' : 'Pause video'); };
        on(v.video, 'play', sync); on(v.video, 'pause', sync);
        on(v.play, 'click', () => {
          if (v.video.paused) { v.paused = false; ensureSrc(v); v.video.play().catch(() => {}); }
          else { v.paused = true; v.video.pause(); }
        });
        sync();
      });

      const apply = () => {
        const shown = items[active];
        vids.forEach((v) => {
          const playing = visible && shown.contains(v.box) && !v.paused;
          if (playing) { ensureSrc(v); v.video.play().catch(() => { v.paused = true; }); }
          else v.video.pause();
        });
      };
      const select = (j) => {
        active = j;
        markSelected(slides, j, 'aria-selected');
        items.forEach((item, k) => { item.hidden = k !== j; item.classList.toggle('is-active', k === j); });
        apply();
      };
      bindGroup(slides, select, on);
      return { setVisible(v) { if (v === visible) return; visible = v; apply(); } };
    }
  }

  /* ---------- Email automation studio ---------- */
  const INDUSTRIES = ['Agency', 'Energy', 'Finance', 'Food & Beverage', 'Healthcare / Pharma', 'Real Estate', 'Tech / Innovation', 'Travel / Tourism / Hospitality', 'Other / General'];
  const PAID_ROUTES = [
    ['Google Ads · Exhibition page', 'Google Ads', 'Exhibition stand'],
    ['Google Ads · Content page', 'Google Ads', 'Content & video'],
    ['Google Ads · AV / tech page', 'Google Ads', 'AV & event tech'],
    ['Meta · Event tech carousel', 'Meta lead form', 'AV & event tech'],
    ['Meta · General event ads', 'Meta lead form', 'Full turnkey'],
    ['Meta · Event-specific ad', 'Meta lead form', 'Event campaign'],
    ['LinkedIn · Event-specific form', 'LinkedIn lead form', 'Event campaign']
  ];
  const EVENTS = ['Healthcare exhibition', 'Technology exhibition', 'Food & beverage show', 'Energy exhibition'];
  const ICONS = {
    trigger: '<path d="M13 3 5 13.5h6L10 21l8-10.5h-6z"/>',
    branch: '<path d="M7 4v4a5 5 0 0 0 5 5 5 5 0 0 0 5-5V4M12 13v7"/>',
    flow: '<path d="m12 4 8 4.5-8 4.5-8-4.5zM4 13l8 4.5 8-4.5"/>',
    email: '<rect x="3.5" y="5.5" width="17" height="13" rx="2.5"/><path d="m4 7 8 6 8-6"/>',
    continues: '<path d="M19.5 12a7.5 7.5 0 1 1-2.2-5.3M19.5 4.5v4.6h-4.6"/>',
    clock: '<circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3 2"/>'
  };
  const KIND = { trigger: 'Trigger', branch: 'Decision', flow: 'Journey', email: 'Email', continues: 'Ongoing' };
  const KIND_LONG = { trigger: 'Journey trigger', branch: 'Routing decision', flow: 'Nurture journey', email: 'Email communication', continues: 'Ongoing relationship' };
  const esc = (value) => String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const svg = (name) => `<svg viewBox="0 0 24 24" aria-hidden="true">${ICONS[name]}</svg>`;

  const MODES = {
    ongoing: {
      title: 'Relevant at every step.',
      intro: 'Existing leads are segmented by industry. Each segment gets its own introduction, then ongoing relevant communication. The agency sequence is expanded here.',
      steps: ['Lead enters from the existing database', 'Routed to one of nine industry segments', 'Three-email introduction, a week apart', 'Weekly relevant communication'],
      lanes: [[0, 0, 'Entry'], [1, 1, 'Routing'], [2, 2, 'Journeys'], [3, 4, 'Agency sequence and ongoing']],
      grid: [[0, 1], [1, 1], [2, 0], [2, 1], [2, 2], [3, 0], [4, 0], [4, 1], [4, 2]],
      links: [[0, 1], [1, 2], [1, 3], [1, 4], [2, 5], [5, 6], [6, 7], [7, 8], [3, 8], [4, 8]],
      nodes: () => [
        N('Existing database leads', 'trigger', 'Existing leads enter relevant ongoing communication.'),
        N('Segment by industry', 'branch', 'Every lead is routed to one of nine nurture segments.', '', INDUSTRIES),
        N('Agency partner nurture', 'flow', 'Agency partners receive their own ongoing sequence. This representative route is expanded in the diagram.'),
        N('Seven sector journeys', 'flow', 'Each sector has its own three-email introduction, then weekly communication.', '', INDUSTRIES.slice(1, 8)),
        N('General nurture', 'flow', 'Other / General leads receive a dedicated introduction and ongoing general communication.'),
        N('Agency email 1', 'email', 'Open a relevant conversation with work and ideas for agency partners.'),
        N('Agency email 2', 'email', 'Show production capability, fabrication and technology.', '1 week later'),
        N('Agency email 3', 'email', 'Invite the recipient to brief ONE about their next event.', '1 week later'),
        N('Ongoing relevant emails', 'continues', 'Each segment continues its own relevant industry communication between briefs.', 'Weekly')
      ]
    },
    paid: {
      title: 'Interest becomes a conversation.',
      intro: 'Every paid enquiry is acknowledged immediately, then routed by source and interest into service follow-up or a matching event campaign, before joining ongoing nurture.',
      steps: ['Enquiry arrives from Google, Meta or LinkedIn', 'Instant confirmation email', 'Service follow-up or event campaign', 'Hand-off to ongoing industry nurture'],
      lanes: [[0, 0, 'Entry'], [1, 1, 'Acknowledge'], [2, 2, 'Source'], [3, 3, 'Follow-up'], [4, 4, 'Ongoing']],
      grid: [[0, 1], [1, 1], [2, 0], [2, 1], [2, 2], [3, 0.5], [3, 1.5], [4, 1]],
      links: [[0, 1], [1, 2], [1, 3], [1, 4], [2, 5], [3, 5], [3, 6], [4, 6], [5, 7], [6, 7]],
      nodes: () => [
        N('New paid lead', 'trigger', 'A new enquiry arrives through Google, Meta or LinkedIn.'),
        N('Confirmation email', 'email', 'Acknowledge receipt and explain the next step.', 'Immediate'),
        N('Google Ads', 'branch', 'Search enquiries are routed by the landing page they came from.', '', routes('Google Ads')),
        N('Meta lead form', 'branch', 'Meta lead forms are routed by the campaign that generated them.', '', routes('Meta lead form')),
        N('LinkedIn lead form', 'branch', 'Event-specific LinkedIn forms feed the matching event campaign.', '', routes('LinkedIn lead form')),
        N('Service follow-up · 3 emails', 'flow', 'Four distinct flows: Exhibition stand; Content & video; AV & event tech; Full turnkey. Each shares relevant work, delivery capability and an invitation to send a brief.', '2, then 5, then 7 days'),
        N('Matching event campaign', 'flow', 'Event-specific Meta ads and LinkedIn forms join the scheduled event campaign. Paid forms that mention an event also feed it.', 'Scheduled event date'),
        N('Relevant ongoing nurture', 'continues', 'After follow-up, leads enter the relevant ongoing industry journey.')
      ]
    },
    events: {
      title: 'The right message, on time.',
      intro: 'Three audience signals feed a scheduled event campaign: four emails timed around the show, from first introduction to an invitation to brief ONE.',
      steps: ['A database, ad or form signal links a lead to an event', 'Lead joins that event’s scheduled campaign', 'Four emails, two weeks apart'],
      lanes: [[0, 0, 'Signals'], [1, 1, 'Campaign'], [2, 4, 'Scheduled emails']],
      grid: [[0, 0], [0, 1], [0, 2], [1, 1], [2, 1], [3, 1], [4, 1], [4, 2]],
      links: [[0, 3], [1, 3], [2, 3], [3, 4], [4, 5], [5, 6], [6, 7]],
      nodes: () => [
        N('Database lead tagged for event', 'trigger', 'An existing lead is associated with an upcoming event.'),
        N('Event-specific ad or form', 'trigger', 'Meta event ads and LinkedIn event-specific forms feed the matching campaign.'),
        N('Paid form mentions event', 'trigger', 'A paid enquiry that names an event joins its scheduled campaign.'),
        N('Scheduled event campaign', 'branch', 'Event journeys are scheduled around the show. Generic examples:', '', EVENTS),
        N('Event email 1', 'email', 'Introduce ONE’s exhibition and experience capabilities for the event.', 'Scheduled date'),
        N('Event email 2', 'email', 'Share concept-to-fabrication work relevant to the event.', '2 weeks later'),
        N('Event email 3', 'email', 'Introduce AV and interactive technology suited to the audience.', '2 weeks later'),
        N('Event email 4', 'email', 'Invite the recipient to share a brief and plan their production timeline.', '2 weeks later')
      ]
    }
  };
  function N(label, kind, body, delay = '', list = null) { return { label, kind, body, delay, list }; }
  function routes(source) { return PAID_ROUTES.filter((r) => r[1] === source).map((r) => `${r[0]} → ${r[2]}`); }
  /* Orthogonal path through points with rounded corners. */
  function roundedPath(pts, r = 12) {
    let d = `M${pts[0][0]},${pts[0][1]}`;
    for (let i = 1; i < pts.length; i++) {
      const [x, y] = pts[i];
      if (i === pts.length - 1) { d += ` L${x},${y}`; break; }
      const [px, py] = pts[i - 1], [nx, ny] = pts[i + 1];
      const r1 = Math.min(r, Math.hypot(x - px, y - py) / 2, Math.hypot(nx - x, ny - y) / 2);
      const ax = x - Math.sign(x - px) * r1, ay = y - Math.sign(y - py) * r1;
      const bx = x + Math.sign(nx - x) * r1, by = y + Math.sign(ny - y) * r1;
      d += ` L${ax},${ay} Q${x},${y} ${bx},${by}`;
    }
    return d;
  }

  function initAutomation({ root, on, observe, cleanups, later }) {
    const mail = $('[data-ox-mail]', root);
    if (!mail) return;
    const modes = $$('[data-ox-mode]', mail), canvas = $('[data-ox-canvas]', mail), flow = $('[data-ox-flow]', mail), detail = $('[data-ox-detail]', mail), output = $('[data-ox-zoom-label]', mail);
    const thumb = $('.ox-modes__thumb', mail), peek = $('.oc-mascot-peek--mail', mail);
    const compact = window.matchMedia('(max-width:989px)');
    let mode = 'ongoing', data, nodes = [], links = [], rects = [], graphWidth = 1184, graphHeight = 500, fitScale = 1, scale = 1, x = 0, y = 0, drag = null, dragged = false, selected = -1, visible = false;
    const NODE_W = 188, COL = 220, NODE_H = 72, ROW = 140, TOP = 60, LEFT = 20;

    const constrain = () => {
      x = clamp(x, Math.min(16, canvas.clientWidth - graphWidth * scale - 16), Math.max(16, canvas.clientWidth - graphWidth * scale - 16));
      y = clamp(y, Math.min(16, canvas.clientHeight - graphHeight * scale - 16), Math.max(16, canvas.clientHeight - graphHeight * scale - 16));
    };
    const paint = () => { constrain(); flow.style.transform = `translate(${x}px,${y}px) scale(${scale})`; output.textContent = `${Math.round(scale * 100)}%`; canvas.classList.toggle('is-zoomed', scale > fitScale + 0.01); };
    /* Compact screens open on a readable view (fit to width, anchored at the top); the Fit button shows the whole graph. */
    const fit = (whole) => {
      const bottomSpace = compact.matches ? 64 : 76;
      if (!compact.matches) canvas.style.minHeight = `${Math.max(420, Math.round(graphHeight * Math.min(1, (canvas.clientWidth - 32) / graphWidth) + bottomSpace + 32))}px`;
      fitScale = Math.min(1, (canvas.clientWidth - 32) / graphWidth, (canvas.clientHeight - 16 - bottomSpace) / graphHeight);
      const home = compact.matches && whole !== true;
      scale = home ? Math.max(fitScale, Math.min(0.78, (canvas.clientWidth - 32) / graphWidth)) : fitScale;
      x = (canvas.clientWidth - graphWidth * scale) / 2; y = home ? 16 : Math.max(16, (canvas.clientHeight - bottomSpace - graphHeight * scale) / 2);
      paint();
    };
    const zoom = (dir) => { const next = clamp(scale + dir * 0.15, 0.35, 1.7), ratio = next / scale; x = canvas.clientWidth / 2 - (canvas.clientWidth / 2 - x) * ratio; y = canvas.clientHeight / 2 - (canvas.clientHeight / 2 - y) * ratio; scale = next; paint(); };
    const moveThumb = () => { const b = modes.find((m) => m.getAttribute('aria-selected') === 'true'); if (!b || !thumb) return; thumb.style.width = `${b.offsetWidth}px`; thumb.style.transform = `translateX(${b.offsetLeft}px)`; };

    const layout = () => {
      if (!compact.matches) {
        graphWidth = LEFT * 2 + COL * 4 + NODE_W;
        graphHeight = TOP + ROW * 2 + NODE_H + 28;
        rects = data.grid.map(([c, r]) => ({ x: LEFT + c * COL, y: TOP + r * ROW, w: NODE_W, h: NODE_H }));
        return;
      }
      const w = Math.max(270, canvas.clientWidth - 24), h = 66, cw = (w - 16) / 3, wide = Math.min(w, 300), center = (w - wide) / 2, cols = [0, cw + 8, (cw + 8) * 2];
      graphWidth = w;
      const row = (n) => 18 + n * 108;
      const R = (xx, yy, ww) => ({ x: xx, y: yy, w: ww, h });
      if (mode === 'ongoing') rects = [R(center, row(0), wide), R(center, row(1), wide), ...cols.map((cx) => R(cx, row(2), cw)), R(center, row(3) + 20, wide), R(center, row(4) + 20, wide), R(center, row(5) + 20, wide), R(center, row(6) + 20, wide)];
      else if (mode === 'paid') rects = [R(center, row(0), wide), R(center, row(1), wide), ...cols.map((cx) => R(cx, row(2), cw)), R(0, row(3) + 20, (w - 10) / 2), R((w + 10) / 2, row(3) + 20, (w - 10) / 2), R(center, row(4) + 30, wide)];
      else rects = [...cols.map((cx) => R(cx, row(0), cw)), R(center, row(1) + 10, wide), R(center, row(2) + 10, wide), R(center, row(3) + 10, wide), R(center, row(4) + 10, wide), R(center, row(5) + 10, wide)];
      graphHeight = Math.max(...rects.map((r) => r.y + r.h)) + 18;
    };
    const route = (a, b, k) => {
      const p = rects[a], q = rects[b];
      if (compact.matches) {
        const ax = p.x + p.w / 2, ay = p.y + p.h, bx = q.x + q.w / 2, by = q.y;
        if (mode === 'ongoing' && (a === 3 || a === 4)) {
          const rail = a === 3 ? -6 : graphWidth + 6, side = a === 3 ? q.x : q.x + q.w;
          return roundedPath([[ax, ay], [ax, ay + 14], [rail, ay + 14], [rail, q.y + q.h / 2], [side, q.y + q.h / 2]], 10);
        }
        const mid = ay + Math.max(10, (by - ay) / 2);
        return roundedPath([[ax, ay], [ax, mid], [bx, mid], [bx, by]], 10);
      }
      if (q.x > p.x + p.w) {
        const ax = p.x + p.w, ay = p.y + p.h / 2, bx = q.x, by = q.y + q.h / 2;
        if (Math.abs(ay - by) < 1) return `M${ax},${ay} L${bx},${by}`;
        const mid = ax + (bx - ax) / 2;
        return roundedPath([[ax, ay], [mid, ay], [mid, by], [bx, by]], 14);
      }
      if (Math.abs(q.x - p.x) < 1) return `M${p.x + p.w / 2},${p.y + p.h} L${q.x + q.w / 2},${q.y}`;
      return roundedPath([[p.x + p.w, p.y + p.h / 2], [q.x + q.w / 2, p.y + p.h / 2], [q.x + q.w / 2, q.y]], 14);
    };
    const overview = () => {
      const m = data;
      const list = '';
      detail.innerHTML = `<span class="ox-badge">${svg('flow')}Journey overview</span><h3 class="oc-h3">${esc(m.title)}</h3><p class="oc-p">${esc(m.intro)}</p><ol class="ox-steps" role="list">${m.steps.map((s, i) => `<li><b>${i + 1}</b>${esc(s)}</li>`).join('')}</ol>${list}<p class="oc-p ox-tip">Select any step in the diagram to inspect it.</p>`;
    };
    const selectNode = (index, user) => {
      selected = index;
      const n = nodes[index];
      flow.classList.add('has-selection');
      const linked = new Set();
      links.forEach(([a, b]) => { if (a === index) linked.add(b); if (b === index) linked.add(a); });
      $$('[data-ox-node]', flow).forEach((b, i) => { b.setAttribute('aria-pressed', String(i === index)); b.classList.toggle('is-linked', linked.has(i)); });
      $$('[data-ox-edge]', flow).forEach((p, i) => p.classList.toggle('is-selected', links[i].includes(index)));
      const from = links.filter(([, b]) => b === index).map(([a]) => a), to = links.filter(([a]) => a === index).map(([, b]) => b);
      const linkList = (label, ids) => ids.length ? `<div class="ox-links"><span>${label}</span>${ids.map((i) => `<button type="button" data-ox-jump="${i}">${esc(nodes[i].label)}<i aria-hidden="true">→</i></button>`).join('')}</div>` : '';
      const extra = n.list ? `<p class="ox-chiprow">${n.list.map((s) => `<span class="ox-client">${esc(s)}</span>`).join(' ')}</p>` : '';
      detail.innerHTML = `<span class="ox-badge">${svg(n.kind)}${esc(KIND_LONG[n.kind])}</span><h3 class="oc-h3">${esc(n.label)}</h3><p class="oc-p">${esc(n.body)}</p>${extra}${n.delay ? `<span class="ox-detail-timing">${svg('clock')}${esc(n.delay)}</span>` : ''}${linkList('Comes from', from)}${linkList('Leads to', to)}`;
      if (user) react(peek, later);
    };
    const clearSelection = () => {
      selected = -1; flow.classList.remove('has-selection');
      $$('[data-ox-node]', flow).forEach((b) => { b.setAttribute('aria-pressed', 'false'); b.classList.remove('is-linked'); });
      $$('[data-ox-edge]', flow).forEach((p) => p.classList.remove('is-selected'));
      overview();
    };
    const render = () => {
      data = MODES[mode];
      nodes = data.nodes(); links = data.links; layout(); selected = -1;
      const uid = `${root.id || 'one'}-${mode}`;
      const paths = links.map(([a, b], k) => route(a, b, k));
      const pulses = reduceMotion.matches ? '' : paths.map((_, k) => `<circle class="ox-pulse" r="3"><animateMotion dur="${2.6 + (k % 3) * 0.35}s" begin="-${(k * 0.37 + 0.4).toFixed(2)}s" repeatCount="indefinite" rotate="auto"><mpath href="#${uid}-e${k}"/></animateMotion></circle>`).join('');
      const lanes = compact.matches ? '' : data.lanes.map(([c0, c1, label]) => `<span class="ox-lane" style="left:${LEFT + c0 * COL - 10}px;width:${(c1 - c0) * COL + NODE_W + 20}px;height:${graphHeight}px"><span>${esc(label)}</span></span>`).join('');
      flow.innerHTML = `${lanes}<svg class="ox-connectors" aria-hidden="true" viewBox="0 0 ${graphWidth} ${graphHeight}" width="${graphWidth}" height="${graphHeight}"><defs><marker id="${uid}-arrow" viewBox="0 0 10 10" refX="8.5" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse"><path d="M1 1.5 L8.5 5 L1 8.5 Z"/></marker></defs>${paths.map((d, k) => `<path id="${uid}-e${k}" class="ox-edge" data-ox-edge d="${d}" marker-end="url(#${uid}-arrow)"/>`).join('')}${pulses}</svg>${nodes.map((n, i) => `<button type="button" class="ox-node" data-kind="${n.kind}" data-ox-node="${i}" aria-pressed="false" style="left:${rects[i].x}px;top:${rects[i].y}px;width:${rects[i].w}px">${n.delay ? `<em>${svg('clock')}${esc(n.delay)}</em>` : ''}<span class="ox-node__icon">${svg(n.kind)}</span><span class="ox-node__text"><small>${KIND[n.kind]}</small><strong>${esc(n.label)}</strong></span></button>`).join('')}`;
      flow.style.width = `${graphWidth}px`; flow.style.height = `${graphHeight}px`;
      if (compact.matches) canvas.style.minHeight = '';
      flow.classList.remove('has-selection');
      overview();
      fit();
      syncPulses();
    };
    const syncPulses = () => { const s = $('svg', flow); if (!s || !s.pauseAnimations) return; if (visible) s.unpauseAnimations(); else s.pauseAnimations(); };
    bindGroup(modes, (i) => { markSelected(modes, i, 'aria-selected'); mode = modes[i].dataset.oxMode; $('#ox-flow-panel', mail).setAttribute('aria-labelledby', modes[i].id); moveThumb(); render(); }, on);
    on(flow, 'click', (e) => { const b = e.target.closest('[data-ox-node]'); if (b && !dragged) { const i = Number(b.dataset.oxNode); if (i === selected) clearSelection(); else selectNode(i, true); } dragged = false; });
    on(detail, 'click', (e) => {
      const b = e.target.closest('[data-ox-jump]'); if (!b) return;
      const i = Number(b.dataset.oxJump); selectNode(i, true);
      if (scale > fitScale + 0.01) { const r = rects[i]; x = canvas.clientWidth / 2 - (r.x + r.w / 2) * scale; y = canvas.clientHeight / 2 - (r.y + r.h / 2) * scale; paint(); }
    });
    on(flow, 'focusin', (e) => { const b = e.target.closest('[data-ox-node]'); if (b && scale > fitScale + 0.01) { const r = rects[Number(b.dataset.oxNode)]; x = canvas.clientWidth / 2 - (r.x + r.w / 2) * scale; y = canvas.clientHeight / 2 - (r.y + r.h / 2) * scale; paint(); } });
    on(canvas, 'pointerdown', (e) => { dragged = false; if (e.button !== 0 || e.target.closest('button')) return; drag = { id: e.pointerId, px: e.clientX, py: e.clientY, x, y }; canvas.setPointerCapture(e.pointerId); canvas.classList.add('is-dragging'); });
    on(canvas, 'pointermove', (e) => { if (!drag || e.pointerId !== drag.id) return; const dx = e.clientX - drag.px, dy = e.clientY - drag.py; if (Math.abs(dx) + Math.abs(dy) > 5) dragged = true; x = drag.x + dx; y = drag.y + dy; paint(); });
    const endDrag = () => { drag = null; canvas.classList.remove('is-dragging'); };
    on(canvas, 'pointerup', endDrag); on(canvas, 'pointercancel', endDrag); on(canvas, 'lostpointercapture', endDrag);
    on(canvas, 'keydown', (e) => { if (e.target !== canvas) return; if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', '+', '=', '-', 'Home', 'Escape'].includes(e.key)) e.preventDefault(); if (e.key === 'ArrowLeft') x += 45; if (e.key === 'ArrowRight') x -= 45; if (e.key === 'ArrowUp') y += 45; if (e.key === 'ArrowDown') y -= 45; if (e.key === '+' || e.key === '=') zoom(1); if (e.key === '-') zoom(-1); if (e.key === 'Home') fit(); if (e.key === 'Escape') clearSelection(); paint(); });
    $$('[data-ox-zoom]', mail).forEach((b) => on(b, 'click', () => zoom(Number(b.dataset.oxZoom))));
    on($('[data-ox-fit]', mail), 'click', () => fit(true));
    observe([canvas], (_, v) => { visible = v; syncPulses(); }, { threshold: 0.1 });
    render();
    moveThumb();
    // Recompose only when the canvas width changes, including theme-editor resizing.
    let lastWidth = canvas.clientWidth;
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(() => { moveThumb(); if (lastWidth === canvas.clientWidth) return; lastWidth = canvas.clientWidth; const index = selected; render(); if (index >= 0) selectNode(index); });
      ro.observe(canvas); ro.observe($('.ox-modes', mail));
      cleanups.push(() => ro.disconnect());
    }
  }

  function initResults({ root, observe }) {
    const nums = $$('[data-count]', root);
    observe(nums, (el, inView, io) => {
      if (!inView) return;
      io && io.unobserve(el);
      const target = Number(el.dataset.count);
      const decimals = Number(el.dataset.decimals || 0);
      const suffix = el.dataset.suffix || '';
      if (reduceMotion.matches) return;
      const start = performance.now();
      const dur = 1600;
      const tick = (now) => {
        const p = clamp((now - start) / dur, 0, 1);
        const v = target * (1 - Math.pow(1 - p, 3));
        el.textContent = v.toFixed(decimals) + suffix;
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, { threshold: 0.6 });
  }

  const boot = () => $$('[data-one-cs]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', (e) => init($('[data-one-cs]', e.target)));
  document.addEventListener('shopify:section:unload', (e) => {
    const root = $('[data-one-cs]', e.target);
    if (root && root.ocDestroy) root.ocDestroy();
  });
})();
